import React, { useRef, useState, useEffect } from 'react';
import { fabric } from 'fabric';
import { toast } from 'react-toastify';
import * as pdfjsLib from 'pdfjs-dist';
import { addCertificateTemplatePlaceholders } from '../../../services/CommonService';

// Custom styles for the Custom Field Section
const customFieldStyles = `
  .custom-field-input:focus + .input-focus-indicator {
    opacity: 1;
    transform: scaleX(1);
  }
  
  .custom-field-input::placeholder {
    color: #adb5bd;
    transition: color 0.3s ease;
  }
  
  .custom-field-input:focus::placeholder {
    color: #6c757d;
  }
  
  .input-focus-indicator {
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #007bff, #0056b3);
    opacity: 0;
    transform: scaleX(0);
    transition: all 0.3s ease;
    border-radius: 1px;
  }
  
  .custom-field-button-container {
    position: relative;
  }
  
  .custom-field-btn {
    padding: 10px 16px;
    font-size: 0.875rem;
    letter-spacing: 0.3px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .custom-field-btn.btn-primary {
    animation: buttonPulse 2s infinite;
  }
  
  @keyframes buttonPulse {
    0%, 100% {
      box-shadow: 0 4px 12px rgba(0,123,255,0.3);
    }
    50% {
      box-shadow: 0 4px 20px rgba(0,123,255,0.5);
    }
  }
  
  .custom-field-btn:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
  }
  
  .custom-field-btn:hover:before {
    left: 100%;
  }
  
  .custom-field-btn.btn-primary {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(0,123,255,0.3);
  }
  
  .custom-field-btn.btn-primary:hover {
    background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0,123,255,0.4);
  }
  
  .custom-field-btn.btn-outline-secondary {
    border: 1px solid #6c757d;
    color: #6c757d;
    background: #ffffff;
  }
  
  .custom-field-btn.btn-outline-secondary:hover {
    background: #6c757d;
    color: #ffffff;
    border-color: #6c757d;
  }
  
  .drag-hint {
    background: rgba(0,123,255,0.1);
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid rgba(0,123,255,0.2);
    animation: fadeInUp 0.3s ease;
  }
  
  .drag-hint small {
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .custom-field-input-container .input-group:focus-within .input-group-text {
    border-color: #007bff;
    background: linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%);
  }

  /* Field Properties Panel Styles */
  .field-properties-panel {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  .field-properties-panel h6 {
    color: #495057;
    font-size: 0.875rem;
    letter-spacing: 0.5px;
    margin-bottom: 20px;
  }

  .field-name-input-container {
    position: relative;
  }

  .field-name-input {
    transition: all 0.3s ease;
    border: 1px solid #dee2e6;
  }

  .field-name-input:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
    outline: none;
    background: #ffffff;
  }

  .font-size-slider-container {
    position: relative;
  }

  .custom-range {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    outline: none;
  }

  .custom-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 18px;
    width: 18px;
    border-radius: 50%;
    background: #007bff;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  .custom-range::-moz-range-thumb {
    height: 18px;
    width: 18px;
    border-radius: 50%;
    background: #007bff;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  .font-size-label {
    font-size: 0.75rem;
    color: #6c757d;
  }

  .font-size-value {
    font-size: 0.875rem;
    color: #007bff;
  }

  .toggle-switch-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
  }

  .toggle-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin: 0;
  }

  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
  }

  .toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #e9ecef;
    transition: 0.3s;
    border-radius: 24px;
  }

  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  .toggle-input:checked + .toggle-slider {
    background-color: #007bff;
  }

  .toggle-input:checked + .toggle-slider:before {
    transform: translateX(20px);
  }

  .color-picker-container {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .color-swatch {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 2px solid #dee2e6;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .color-swatch:hover {
    border-color: #007bff;
    transform: scale(1.05);
  }

  .color-input {
    flex: 1;
    transition: all 0.3s ease;
  }

  .color-input:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
    outline: none;
  }

  .delete-button-container {
    margin-top: 24px;
  }

  .delete-field-btn {
    transition: all 0.3s ease;
    font-weight: 500;
  }

  .delete-field-btn:hover {
    background-color: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220,53,69,0.3);
  }

  .delete-field-btn:active {
    transform: translateY(0);
  }

  /* New Layout Styles */
  .section-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    border: 1px solid #f0f0f0;
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .input-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6c757d;
    margin-bottom: 8px;
    display: block;
  }

  .field-input {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
  }

  .field-input:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
    outline: none;
    background-color: #ffffff;
  }

  .add-field-btn {
    border-radius: 8px;
    padding: 12px 16px;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .add-field-btn.btn-primary {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(0,123,255,0.3);
  }

  .add-field-btn.btn-primary:hover {
    background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0,123,255,0.4);
  }

  .fields-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .field-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .field-item:hover {
    background: #e9ecef;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .field-item.active {
    background: #fff3cd;
    border-color: #ffc107;
    box-shadow: 0 2px 8px rgba(255,193,7,0.2);
  }

  .field-type {
    background: #6c757d;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
  }

  .field-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
  }

  /* Marksheet Template Styles */
  .marksheet-template {
    background: #fefefe;
    border-radius: 16px;
    padding: 40px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.1);
    border: 1px solid #f0f0f0;
    max-width: 800px;
    margin: 0 auto;
  }

  .template-header {
    text-align: center;
    margin-bottom: 40px;
    padding-bottom: 30px;
    border-bottom: 2px solid #f0f0f0;
  }

  .logo-circle {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    border-radius: 50%;
    margin: 0 auto 20px;
    box-shadow: 0 4px 16px rgba(255,215,0,0.3);
  }

  .template-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  .school-name {
    font-size: 1.25rem;
    color: #6c757d;
    margin-bottom: 5px;
    font-weight: 500;
  }

  .exam-info {
    font-size: 1rem;
    color: #6c757d;
    font-weight: 400;
  }

  .template-content {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  .student-details h3,
  .marks-table h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #f0f0f0;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f8f9fa;
  }

  .detail-label {
    font-weight: 600;
    color: #495057;
    font-size: 1rem;
  }

  .detail-value {
    color: #6c757d;
    font-size: 1rem;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
  }

  .table th {
    background: #fff3cd;
    color: #856404;
    padding: 15px 12px;
    text-align: left;
    font-weight: 600;
    border-bottom: 2px solid #ffeaa7;
  }

  .table td {
    padding: 12px;
    border-bottom: 1px solid #f8f9fa;
    color: #495057;
  }

  .table tr:hover {
    background: #f8f9fa;
  }

  /* No Selection State */
  .no-selection-state {
    text-align: center;
    padding: 40px 20px;
  }

  .no-selection-icon i {
    font-size: 4rem;
    color: #dee2e6;
  }

  .no-selection-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 12px;
  }

  .no-selection-text {
    color: #adb5bd;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  /* No Template State */
  .no-template-state {
    text-align: center;
    padding: 60px 20px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.05);
    border: 2px dashed #dee2e6;
  }

  .no-template-icon i {
    font-size: 5rem;
    color: #dee2e6;
    margin-bottom: 20px;
  }

  .no-template-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 16px;
  }

  .no-template-text {
    color: #adb5bd;
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }


  /* PDF Canvas Container */
  .pdf-canvas-container {
    border-radius: 12px;
    overflow-y: auto;
    overflow-x: hidden;
    background: #ffffff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    border: 2px solid #e9ecef;
    max-height: 80vh;
  }

  /* Canvas Header */
  .canvas-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
  }

  .canvas-title {
    margin: 0;
    font-weight: 600;
    color: #495057;
  }

  .canvas-loading {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
  }

  /* Canvas Wrapper */
  .canvas-wrapper {
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    margin: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Canvas Element */
  #pdfDisplayCanvas {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #ffffff;
    min-height: 400px;
    min-width: 300px;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    image-rendering: pixelated;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* High DPI Canvas Support */
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    #pdfDisplayCanvas {
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .marksheet-template {
      padding: 20px;
      margin: 10px;
    }
    
    .template-title {
      font-size: 2rem;
    }
    
    .section-card {
      padding: 15px;
    }

    .no-template-state {
      padding: 40px 20px;
    }
  }
`;

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const DEFAULT_PDF_DIMENSIONS = { width: 595, height: 842 };

const CertificateFillable = ({
  isOpen,
  onClose,
  pdfDisplayCanvas,
  templateUuid,
  templateName,
  onPlaceholdersSaved
}) => {
  const [canvas, setCanvas] = useState(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfPage, setPdfPage] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState(DEFAULT_PDF_DIMENSIONS);
  const [draggedItemType, setDraggedItemType] = useState(null);
  const [noFieldSelectionFlag, setNoFieldSelectionFlag] = useState(true);
  const [selectedTextContent, setSelectedTextContent] = useState('');
  const [visibleTextContentTextarea, setVisibleTextContentTextarea] = useState(false);
  const [templateUuidState, setTemplateUuidState] = useState(templateUuid || '');
  const [loading, setLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [canvasKey, setCanvasKey] = useState(0);
  // New state for field properties
  const [selectedFieldProperties, setSelectedFieldProperties] = useState({
    fieldName: 'Roll Number',
    fontSize: 14,
    bold: true,
    italic: false,
    colorCode: '#000000'
  });

  const canvasRef = useRef();

  // Update template UUID and reset content on open
  useEffect(() => {
    try {
      setLoading(true);
      setTemplateUuidState(templateUuid || '');
      setSelectedTextContent('');

      // Reset PDF states when opening
      if (isOpen) {
        setPdfLoaded(false);
        setPdfPage(null);
        setLoading(false);
        setPdfError('');
        setPdfDimensions(DEFAULT_PDF_DIMENSIONS);
        setCanvasKey(prev => prev + 1); // Force canvas re-creation
      }
      setLoading(false);
    } catch (error) {
      console.error('Error updating template UUID:', error);
      setLoading(false);
    }
  }, [templateUuid, isOpen]);

  // Load PDF every time uploadedFileUrl changes
  useEffect(() => {
    if (pdfDisplayCanvas && isOpen) {
      console.log('PDF display canvas changed, loading PDF:', pdfDisplayCanvas);
      console.log('Canvas element exists:', !!document.getElementById('pdfDisplayCanvas'));
      setLoading(true);
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        loadPDFFromUrl(pdfDisplayCanvas);
      }, 100);
    }
  }, [pdfDisplayCanvas, isOpen]);

  // Cleanup canvas when component unmounts or closes
  useEffect(() => {
    return () => {
      if (canvas) {
        try {
          canvas.clear();
          canvas.dispose();
        } catch (error) {
          console.error('Error during canvas cleanup:', error);
        }
        setCanvas(null);
      }
    };
  }, [canvas, isOpen]);

  // Utility function to check if canvas is ready
  const isCanvasReady = () => {
    try {
      console.log('isCanvasReady check:', { canvas: !!canvas, canvasType: canvas?.constructor?.name });
      if (!canvas) {
        return false;
      }

      const ctx = canvas.getContext('2d');
      const isReady = ctx && typeof ctx.clearRect === 'function';
      console.log('Canvas ready state:', isReady);
      return isReady;
    } catch (error) {
      console.error('Error in isCanvasReady:', error);
      return false;
    }
  };

  // Initialize Fabric Canvas
  useEffect(() => {
    console.log('Canvas initialization effect triggered:', { isOpen, pdfDimensions, canvasKey });
    if (isOpen && pdfDimensions.width > 0 && pdfDimensions.height > 0) {
      setLoading(true);

      // Clean up existing canvas first
      if (canvas) {
        try {
          canvas.clear();
          canvas.dispose();
          setCanvas(null);
        } catch (error) {
          console.error('Error disposing existing canvas:', error);
        }
      }

      const initializeCanvas = () => {
        try {
          console.log('Initializing canvas...');
          const canvasElement = document.getElementById('pdfDisplayCanvas');
          console.log('Canvas element found:', !!canvasElement);
          if (canvasElement) {
            console.log('Canvas element properties:', {
              width: canvasElement.width,
              height: canvasElement.height,
              offsetWidth: canvasElement.offsetWidth,
              offsetHeight: canvasElement.offsetHeight
            });
          }

          // Ensure element exists and is properly mounted
          if (!canvasElement || !canvasElement.isConnected || !canvasElement.parentNode) {
            console.warn('Canvas element not ready, retrying...');
            return false;
          }

          // Calculate optimal canvas size to fit within container
          const containerWidth = 1200; // Increased from 800 to 1200 for better fit
          const containerHeight = 600; // Height available

          // Calculate scale to fit PDF within container with better quality
          const scaleX = containerWidth / pdfDimensions.width;
          const scaleY = containerHeight / pdfDimensions.height;
          const scale = Math.min(scaleX, scaleY, 1.2); // Allow slight upscaling for better quality

          const canvasWidth = pdfDimensions.width * scale;
          const canvasHeight = pdfDimensions.height * scale;

          // Set canvas element dimensions with device pixel ratio for crisp rendering
          const devicePixelRatio = window.devicePixelRatio || 1;
          canvasElement.width = canvasWidth * devicePixelRatio;
          canvasElement.height = canvasHeight * devicePixelRatio;
          canvasElement.style.width = canvasWidth + 'px';
          canvasElement.style.height = canvasHeight + 'px';

          const inst = new fabric.Canvas('pdfDisplayCanvas', {
            width: canvasWidth,
            height: canvasHeight,
            selection: true,
            preserveObjectStacking: true,
            renderOnAddRemove: true,
            enableRetinaScaling: true, // Enable retina scaling for better quality
            imageSmoothingEnabled: false, // Disable smoothing for crisp text
          });

          // Set canvas quality settings
          inst.setZoom(1);
          inst.renderAll();

          // Verify canvas is working
          const ctx = inst.getContext('2d');
          if (!ctx) {
            throw new Error('Canvas context not available');
          }

          setCanvas(inst);
          console.log('Fabric canvas initialized successfully with dimensions:', canvasWidth, canvasHeight);
          setLoading(false);
          return true;
        } catch (error) {
          console.error('Error initializing Fabric canvas:', error);
          setLoading(false);
          return false;
        }
      };

      // Use a more robust retry mechanism
      let attempts = 0;
      const maxAttempts = 10;

      const tryInitialize = () => {
        if (initializeCanvas()) {
          return; // Success
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(tryInitialize, 200);
        } else {
          console.error('Failed to initialize canvas after maximum attempts');
          setLoading(false);
        }
      };

      // Start initialization after a small delay to ensure DOM is ready
      const timer = setTimeout(tryInitialize, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen, pdfDimensions.width, pdfDimensions.height, canvasKey]);

  // Load PDF from URL
  const loadPDFFromUrl = async (fileUrl) => {
    try {
      console.log('Loading PDF from URL:', fileUrl);
      console.log('Canvas ready state:', isCanvasReady());
      console.log('PDF dimensions:', pdfDimensions);

      // Validate URL
      if (!fileUrl || typeof fileUrl !== 'string' || fileUrl.trim() === '') {
        throw new Error('Invalid PDF URL provided');
      }

      setLoading(true);
      setLoading(true);
      setPdfError('');

      const pdf = await pdfjsLib.getDocument({
        url: fileUrl,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
        cMapPacked: true,
        standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/',
        enableXfa: false,
        disableFontFace: false,
        useSystemFonts: true
      }).promise;

      const page = await pdf.getPage(1);

      // Use a higher scale for better quality
      const viewport = page.getViewport({ scale: 1.5 });

      console.log('PDF loaded, dimensions:', viewport.width, viewport.height);

      setPdfDimensions({
        width: viewport.width,
        height: viewport.height,
      });
      setPdfPage(page);
      setPdfLoaded(true);
      setLoading(false);
      setLoading(false);
      toast.success('PDF loaded successfully!');
    } catch (error) {
      console.error('PDF loading error:', error);
      const errorMessage = error.message === 'Invalid PDF URL provided'
        ? 'Invalid PDF URL provided'
        : 'Failed to load PDF. Please ensure the file is accessible and try again.';
      setPdfError(errorMessage);
      setLoading(false);
      setLoading(false);
      toast.error(errorMessage);
    }
  };

  // Render the PDF as Canvas background
  useEffect(() => {
    if (pdfLoaded && pdfPage && isCanvasReady()) {
      renderPDFToCanvas();
    }
  }, [pdfLoaded, pdfPage, canvas]);

  // Function to render PDF to canvas
  const renderPDFToCanvas = async () => {
    console.log('renderPDFToCanvas called:', { pdfPage: !!pdfPage, canvasReady: isCanvasReady() });
    if (!pdfPage || !isCanvasReady()) {
      console.warn('Canvas or PDF not ready for rendering');
      return;
    }

    try {
      setLoading(true);
      console.log('Rendering PDF to canvas...');

      // Get the visible canvas element (managed by Fabric) to sync dimensions
      const canvasElement = document.getElementById('pdfDisplayCanvas');

      // Calculate optimal scale to fit within container while maintaining quality
      const containerWidth = 800; // Width for col-md-6
      const containerHeight = 600; // Height available

      // Use a higher scale factor for better quality
      const scaleX = containerWidth / pdfDimensions.width;
      const scaleY = containerHeight / pdfDimensions.height;
      const scale = Math.min(scaleX, scaleY, 1.5); // Allow slight upscaling for better quality

      const viewport = pdfPage.getViewport({ scale });

      if (canvasElement) {
        // Set canvas dimensions with device pixel ratio for crisp rendering
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvasElement.width = viewport.width * devicePixelRatio;
        canvasElement.height = viewport.height * devicePixelRatio;
        canvasElement.style.width = viewport.width + 'px';
        canvasElement.style.height = viewport.height + 'px';

        // Get the 2D context and scale it for high DPI displays
        const ctx = canvasElement.getContext('2d');
        ctx.scale(devicePixelRatio, devicePixelRatio);
      }

      // Update Fabric canvas dimensions
      canvas.setDimensions({ width: viewport.width, height: viewport.height });

      // Render the PDF page onto an offscreen canvas with high quality
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = viewport.width;
      offscreenCanvas.height = viewport.height;
      const offscreenCtx = offscreenCanvas.getContext('2d');

      // Set high quality rendering context
      offscreenCtx.imageSmoothingEnabled = false; // Disable smoothing for crisp text
      offscreenCtx.textRenderingOptimization = 'optimizeQuality';

      await pdfPage.render({
        canvasContext: offscreenCtx,
        viewport,
        enableWebGL: false, // Disable WebGL for better compatibility
        renderInteractiveForms: false
      }).promise;

      // Convert offscreen canvas to high-quality image
      const imageDataUrl = offscreenCanvas.toDataURL('image/png', 1.0); // Maximum quality

      // Clear existing canvas content with safety check
      if (isCanvasReady()) {
        canvas.clear();
      }

      // Set the PDF as background image with proper positioning
      fabric.Image.fromURL(imageDataUrl, (img) => {
        if (isCanvasReady()) {
          img.set({
            originX: 'left',
            originY: 'top',
            scaleX: 1,
            scaleY: 1,
            selectable: false,
            evented: false,
            crossOrigin: 'anonymous'
          });

          canvas.setBackgroundImage(img, () => {
            canvas.renderAll();
            console.log('PDF set as background image with dimensions:', viewport.width, viewport.height);
            setLoading(false);
          });
        }
      }, { crossOrigin: 'anonymous' });

    } catch (error) {
      console.error('PDF rendering error:', error);
      setLoading(false);
      toast.error('Failed to render PDF on canvas');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, type) => {
    if (type === "custom-textbox" && selectedTextContent.trim() === "") {
      e.preventDefault();
      toast.error('Please enter field name first');
      return;
    }

    // Add visual feedback for drag start
    if (e.target) {
      e.target.style.transform = 'scale(0.95)';
      e.target.style.opacity = '0.8';
    }

    setDraggedItemType(type);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', type);
      e.dataTransfer.setData('application/json', JSON.stringify({ type }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!isCanvasReady()) {
      console.warn('Canvas not ready for dropping fields');
      toast.error('Canvas not ready. Please wait and try again.');
      return;
    }

    try {
      setLoading(true);
      const rect = e.target.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      addFieldToCanvas(draggedItemType, x, y);
      setDraggedItemType(null);

      // Show success message with field name
      if (draggedItemType === 'custom-textbox' && selectedTextContent.trim()) {
        toast.success(`"${selectedTextContent}" field added successfully!`);
      } else {
        toast.success('Field added successfully!');
      }
    } catch (error) {
      console.error('Error during drop operation:', error);
      setLoading(false);
      toast.error('Failed to add field');
    }
  };

  const handleDragEnd = (e) => {
    // Reset button appearance after drag
    if (e.target) {
      e.target.style.transform = 'scale(1)';
      e.target.style.opacity = '1';
    }
  };

  // Create and add field to canvas
  const addFieldToCanvas = (type, left, top) => {
    if (!isCanvasReady()) {
      console.warn('Canvas not ready for adding fields');
      toast.error('Canvas not ready. Please wait and try again.');
      return;
    }

    try {
      setLoading(true);

      let text = '';
      switch (type) {
        case 'custom-textbox':
          text = selectedTextContent || 'Custom Field';
          // Clear the input after successful field creation
          setSelectedTextContent('');
          break;
        case 'static-textbox':
          text = selectedTextContent || 'Static Field';
          // Clear the input after successful field creation
          setSelectedTextContent('');
          break;
        default:
          text = 'Field';
      }

      // Smaller rectangle
      const rect = new fabric.Rect({
        width: 100,       // small width
        height: 30,       // small height
        left: left,
        top: top,
        fill: '#f5f5f5',
        stroke: "#ccc",
        strokeWidth: 1,
        rx: 4,            // rounded corners
        ry: 4,
        selectable: true,
        evented: true,
      });

      // Label text inside the rectangle
      const label = new fabric.Text(text, {
        fontSize: 12,
        fill: "#333",
        fontFamily: "Arial",
        originX: "center",
        originY: "center",
        left: left + 50,   // center align text horizontally
        top: top + 15,     // center align text vertically
      });

      // Apply default properties to the label
      applyDefaultProperties(label);

      // Group rect + label only
      const group = new fabric.Group([rect, label], {
        left,
        top,
        selectable: true,
      });

      canvas.add(group);
      canvas.setActiveObject(group);
      canvas.renderAll();
      setNoFieldSelectionFlag(false);
      setVisibleTextContentTextarea(type === 'custom-textbox' || type === 'static-textbox');
      setLoading(false);
    } catch (error) {
      console.error('Error adding field to canvas:', error);
      setLoading(false);
      toast.error('Failed to add field to canvas');
    }
  };

  // Collect placeholders for saving
  const collectPlaceholders = () => {
    try {
      setLoading(true);
      if (!isCanvasReady()) {
        setLoading(false);
        return [];
      }

      const placeholders = canvas.getObjects().filter(obj => obj.type === 'group').map(obj => {
        const rect = obj.getBoundingRect();
        let textLabel = obj._objects?.find(item => item.type === 'text');
        return {
          text_content: textLabel?.text || '',
          position: {
            left: Math.round(rect.left),
            top: Math.round(rect.top),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
          },
          font_size: textLabel?.fontSize || 14,
          bold: textLabel?.fontWeight === 'bold',
          italic: textLabel?.fontStyle === 'italic',
          color_code: textLabel?.fill || "#000000"
        };
      });

      setLoading(false);
      return placeholders;
    } catch (error) {
      console.error('Error collecting placeholders:', error);
      setLoading(false);
      return [];
    }
  };

  // Save action
  const handleSave = async () => {
    if (!templateUuidState) {
      toast.error('Template UUID is required to save placeholders');
      return;
    }
    const placeholders = collectPlaceholders();
    if (!placeholders.length) {
      toast.error('No placeholders found. Please add some fields first.');
      return;
    }
    try {
      setLoading(true);
      const response = await addCertificateTemplatePlaceholders({
        template_uuid: templateUuidState,
        placeholders,
      });
      if (response && response.data) {
        toast.success(response.data.message || 'Certificate placeholders saved!');

        // Auto-download CSV file if available
        if (response.data.csv_url) {
          downloadCSV(response.data.csv_url);
        }

        // notify parent to refresh cards
        if (typeof onPlaceholdersSaved === 'function') {
          onPlaceholdersSaved();
        }
      } else {
        toast.error('Failed to save placeholders');
      }
    } catch {
      toast.error('Failed to save placeholders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to download CSV file
  const downloadCSV = (csvUrl) => {
    try {
      setLoading(true);
      const link = document.createElement('a');
      link.href = csvUrl;
      link.download = 'certificate_placeholders.csv';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV file downloaded successfully!');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV file');
    } finally {
      setLoading(false);
    }
  };

  const clearCanvas = () => {
    if (!isCanvasReady()) {
      console.warn('Canvas not ready for clearing');
      return;
    }

    try {
      setLoading(true);
      canvas.clear();
      setSelectedTextContent('');
      setNoFieldSelectionFlag(true);
      setVisibleTextContentTextarea(false);

      // Reset properties to defaults
      setSelectedFieldProperties({
        fieldName: 'Roll Number',
        fontSize: 14,
        bold: true,
        italic: false,
        colorCode: '#000000'
      });

      if (pdfLoaded && pdfPage && isCanvasReady()) {
        renderPDFToCanvas();
      } else {
        setLoading(false);
        toast.success('Canvas cleared!');
      }
    } catch (error) {
      console.error('Error clearing canvas:', error);
      setLoading(false);
      toast.error('Failed to clear canvas');
    }
  };

  // Reset field properties to defaults
  const resetToDefaultProperties = () => {
    try {
      setLoading(true);
      const defaultProps = {
        fieldName: 'Roll Number',
        fontSize: 14,
        bold: true,
        italic: false,
        colorCode: '#000000'
      };

      setSelectedFieldProperties(defaultProps);

      // Apply to currently selected field if any
      if (isCanvasReady() && !noFieldSelectionFlag) {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type === 'group') {
          const textObject = activeObject._objects?.find(item => item.type === 'text');
          if (textObject) {
            textObject.set({
              fontSize: defaultProps.fontSize,
              fontWeight: defaultProps.bold ? 'bold' : 'normal',
              fontStyle: defaultProps.italic ? 'italic' : 'normal',
              fill: defaultProps.colorCode
            });
            canvas.renderAll();
            toast.success('Properties reset to defaults!');
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error resetting properties:', error);
      setLoading(false);
      toast.error('Failed to reset properties');
    }
  };

  // Remove selected field
  const handleDeleteField = () => {
    try {
      setLoading(true);
      if (isCanvasReady()) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          canvas.remove(activeObject);
          canvas.renderAll();
          setNoFieldSelectionFlag(true);
          setVisibleTextContentTextarea(false);
          toast.success('Field deleted successfully!');
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error deleting field:', error);
      setLoading(false);
      toast.error('Failed to delete field');
    }
  };

  // Handle field property changes
  const handlePropertyChange = (property, value) => {
    try {
      setLoading(true);
      if (!isCanvasReady()) return;

      const activeObject = canvas.getActiveObject();
      if (activeObject && activeObject.type === 'group') {
        const textObject = activeObject._objects?.find(item => item.type === 'text');
        if (textObject) {
          switch (property) {
            case 'fieldName':
              textObject.set('text', value);
              break;
            case 'fontSize':
              textObject.set('fontSize', value);
              break;
            case 'bold':
              textObject.set('fontWeight', value ? 'bold' : 'normal');
              break;
            case 'italic':
              textObject.set('fontStyle', value ? 'italic' : 'normal');
              break;
            case 'colorCode':
              textObject.set('fill', value);
              break;
            default:
              break;
          }

          setSelectedFieldProperties(prev => ({
            ...prev,
            [property]: value
          }));

          canvas.renderAll();
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error changing property:', error);
      setLoading(false);
      toast.error('Failed to change property');
    }
  };

  // Apply default properties to newly created fields
  const applyDefaultProperties = (textObject) => {
    try {
      setLoading(true);
      if (textObject) {
        textObject.set({
          fontSize: selectedFieldProperties.fontSize,
          fontWeight: selectedFieldProperties.bold ? 'bold' : 'normal',
          fontStyle: selectedFieldProperties.italic ? 'italic' : 'normal',
          fill: selectedFieldProperties.colorCode
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error applying default properties:', error);
      setLoading(false);
    }
  };

  // Show field content editing only when appropriate
  useEffect(() => {
    if (isCanvasReady()) {
      const onSelection = () => {
        try {
          setLoading(true);
          const activeObject = canvas.getActiveObject();
          if (activeObject && activeObject.type === 'group') {
            setNoFieldSelectionFlag(false);
            setVisibleTextContentTextarea(true);

            const textObject = activeObject._objects?.find(item => item.type === 'text');
            if (textObject) {
              setSelectedFieldProperties({
                fieldName: textObject.text || 'Roll Number',
                fontSize: textObject.fontSize || 14,
                bold: textObject.fontWeight === 'bold',
                italic: textObject.fontStyle === 'italic',
                colorCode: textObject.fill || '#000000'
              });
            }
          } else {
            setNoFieldSelectionFlag(true);
            setVisibleTextContentTextarea(false);
          }
          setLoading(false);
        } catch (error) {
          console.error('Error handling selection:', error);
          setLoading(false);
        }
      };

      canvas.on('selection:created', onSelection);
      canvas.on('selection:cleared', onSelection);

      return () => {
        if (canvas && canvas.off) {
          canvas.off('selection:created', onSelection);
          canvas.off('selection:cleared', onSelection);
        }
      };
    }
  }, [canvas]);

  if (!isOpen) return null;

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <style>{customFieldStyles}</style>
      <div
        className={`offcanvas offcanvas-end w-100 show`}
        style={{ display: isOpen ? 'block' : 'none', zIndex: 1050, background: '#fff', position: 'fixed', top: 0, right: 0, height: '100vh' }}
        tabIndex="-1"
        id="certificateFillableOffcanvas"
        aria-labelledby="certificateFillableOffcanvasLabel"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="certificateFillableOffcanvasLabel">
            {templateName || "Certificate Fillable"}
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={onClose}
            aria-label="Close"
          > <i className="bi bi-x-circle"></i></button>
        </div>
        <div className="offcanvas-body p-0">
          <div className="certificate-fillable-content">
            <div className="container-fluid h-100 p-0">
              <div className="row g-0 h-100">
                {/* Left Sidebar - Field Management */}
                <div className="col-md-2 border-end bg-white p-4">
                  <div className="d-flex flex-column h-100">

                    {/* Add Custom Field Section */}
                    <div className="add-custom-field-section mb-4">
                      <div className="section-card">
                        <h6 className="section-title">Add Custom Field</h6>
                        <div className="field-input-container mb-3">
                          <label className="input-label">Field name</label>
                          <input
                            type="text"
                            className="form-control field-input"
                            placeholder="Enter field name"
                            value={selectedTextContent}
                            onChange={e => setSelectedTextContent(e.target.value)}
                          />
                        </div>
                        <button
                          id="addFieldButton"
                          draggable={selectedTextContent.trim() !== ""}
                          onDragStart={e => handleDragStart(e, "custom-textbox")}
                          onDragEnd={handleDragEnd}
                          className={`btn w-100 add-field-btn ${selectedTextContent.trim() !== "" ? 'btn-primary' : 'btn-outline-secondary'}`}
                          disabled={selectedTextContent.trim() === ""}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Add Field
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Central Canvas Area */}
                <div className="col-md-8 bg-light p-4">
                  <div className="canvas-container">
                    {/* PDF Canvas for functionality */}
                    <div className="pdf-canvas-container">
                      <div className="canvas-header">

                      </div>
                      <div
                        className="position-relative"
                        ref={canvasRef}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        {draggedItemType && (
                          <div
                            className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center drop-indicator"
                            style={{
                              zIndex: 1000,
                              pointerEvents: "none",
                              backgroundColor: "rgba(0, 123, 255, 0.1)",
                              border: "3px dashed #007bff",
                              borderRadius: "8px",
                            }}
                          >
                            <div className="text-center text-primary">
                              <i className="bi bi-arrow-down-circle fs-1 mb-2"></i>
                              <h5 className="fw-bold">Drop here to add field</h5>
                              <p className="text-muted">
                                Release to place the field on the certificate
                              </p>
                            </div>
                          </div>
                        )}

                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{
                            width: '100%',
                            minHeight: '100%',
                            position: 'relative'
                          }}
                          onDrop={handleDrop}
                          onDragOver={(e) => e.preventDefault()}
                          onDragEnter={(e) => e.preventDefault()}
                        >
                          {pdfError && (
                            <div className="d-flex flex-column align-items-center justify-content-center">
                              <div className="text-danger mb-3">
                                <i className="bi bi-exclamation-triangle fs-1"></i>
                              </div>
                              <p className="text-danger text-center">{pdfError}</p>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => loadPDFFromUrl(pdfDisplayCanvas)}
                              >
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                Retry
                              </button>
                            </div>
                          )}

                          <div
                            className="canvas-wrapper"
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                              marginTop: '10px',
                            }}
                          >
                            <canvas
                              id="pdfDisplayCanvas"
                              key={`canvas-${templateUuidState}-${canvasKey}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                borderRadius: '8px',
                                backgroundColor: '#fff'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Sidebar - Field Properties */}
                <div className="col-md-2 border-start bg-white p-4">
                  {noFieldSelectionFlag ? (
                    <div className="no-selection-state">
                      <div className="no-selection-icon mb-3">
                        <i className="bi bi-pencil-square text-muted"></i>
                      </div>
                      <h5 className="no-selection-title">
                        No Field Selected
                      </h5>
                      <p className="no-selection-text">
                        Select a field to make changes
                      </p>
                    </div>
                  ) : (
                    <div className="field-properties-panel">
                      <h6 className="fw-bold text-uppercase text-muted mb-3">Field Properties</h6>

                      {/* Field Name Section */}
                      <div className="mb-4">
                        <label className="form-label small fw-bold text-muted mb-2">Field Name</label>
                        <div className="field-name-input-container">
                          <input
                            type="text"
                            className="form-control field-name-input"
                            value={selectedFieldProperties.fieldName || 'Roll Number'}
                            onChange={(e) => handlePropertyChange('fieldName', e.target.value)}
                            placeholder="Enter field name"
                          />
                        </div>
                      </div>

                      {/* Font Size Section */}
                      <div className="mb-4">
                        <label className="form-label small fw-bold text-muted mb-2">Font Size</label>
                        <div className="font-size-slider-container">
                          <input
                            type="range"
                            className="form-range custom-range"
                            min="8"
                            max="72"
                            value={selectedFieldProperties.fontSize}
                            onChange={(e) => handlePropertyChange('fontSize', parseInt(e.target.value))}
                          />
                          <div className="d-flex justify-content-between mt-2">
                            <small className="text-muted font-size-label">8</small>
                            <span className="fw-bold text-primary font-size-value">{selectedFieldProperties.fontSize}</span>
                            <small className="text-muted font-size-label">72</small>
                          </div>
                        </div>
                      </div>

                      {/* Text Style Toggles */}
                      <div className="mb-4">
                        {/* Bold Toggle */}
                        <div className="toggle-switch-container mb-3">
                          <label className="toggle-label fw-bold text-muted">Bold</label>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              id="boldToggle"
                              checked={selectedFieldProperties.bold}
                              onChange={(e) => handlePropertyChange('bold', e.target.checked)}
                              className="toggle-input"
                            />
                            <label htmlFor="boldToggle" className="toggle-slider"></label>
                          </div>
                        </div>

                        {/* Italic Toggle */}
                        <div className="toggle-switch-container">
                          <label className="toggle-label fst-italic text-muted">Italic</label>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              id="italicToggle"
                              checked={selectedFieldProperties.italic}
                              onChange={(e) => handlePropertyChange('italic', e.target.checked)}
                              className="toggle-input"
                            />
                            <label htmlFor="italicToggle" className="toggle-slider"></label>
                          </div>
                        </div>
                      </div>

                      {/* Text Color Section */}
                      <div className="mb-4">
                        <label className="form-label small fw-bold text-muted mb-2">Text Color</label>
                        <div className="color-picker-container">
                          <div
                            className="color-swatch"
                            style={{ backgroundColor: selectedFieldProperties.colorCode }}
                          ></div>
                          <input
                            type="text"
                            className="form-control color-input"
                            value={selectedFieldProperties.colorCode}
                            onChange={(e) => handlePropertyChange('colorCode', e.target.value)}
                            placeholder="#000000"
                          />
                        </div>
                      </div>

                      {/* Delete Field Button */}
                      <div className="delete-button-container">
                        <button
                          onClick={handleDeleteField}
                          className="btn btn-danger delete-field-btn"
                        >
                          Delete Field
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="modal-footer border-top py-2">
              <button className="btn btn-outline-warning me-2" onClick={clearCanvas}>
                <i className="bi bi-trash me-1"></i> Clear
              </button>
              <button
                className="btn btn-secondary me-2"
                onClick={onClose}
              >Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!templateUuidState}
              >
                <i className="bi bi-save me-2"></i> Save
              </button>
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <div
          className="offcanvas-backdrop fade show"
          style={{ zIndex: 1040 }}
          onClick={onClose}
        ></div>
      )}
    </>
  );
};

export default CertificateFillable;

